import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { JobCardPriority } from '@/app/types/jobCard';

interface PriorityBadgeProps {
  priority: JobCardPriority;
  className?: string;
  showLabel?: boolean;
}

const priorityConfig: Record<
  JobCardPriority,
  { label: string; bgColor: string; textColor: string; borderColor: string }
> = {
  low: {
    label: 'Low',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  },
  normal: {
    label: 'Normal',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
  },
  high: {
    label: 'High',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-300',
  },
  urgent: {
    label: 'Urgent',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-300',
  },
};

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
  priority,
  className,
  showLabel = true,
}) => {
  const config = priorityConfig[priority] || priorityConfig.normal;

  return (
    <Badge
      variant="outline"
      className={cn(
        config.bgColor,
        config.textColor,
        config.borderColor,
        'font-medium',
        className
      )}
    >
      {showLabel ? config.label : priority}
    </Badge>
  );
};

// Helper component for status badges
interface StatusBadgeProps {
  status: string;
  className?: string;
}

const statusConfig: Record<
  string,
  { label: string; bgColor: string; textColor: string; borderColor: string }
> = {
  arrival: {
    label: 'Arrival',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-300',
  },
  inspection: {
    label: 'Inspection',
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-300',
  },
  quoted: {
    label: 'Quoted',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-300',
  },
  approved: {
    label: 'Approved',
    bgColor: 'bg-lime-100',
    textColor: 'text-lime-700',
    borderColor: 'border-lime-300',
  },
  under_service: {
    label: 'Under Service',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
  },
  quality_check: {
    label: 'Quality Check',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-300',
  },
  ready: {
    label: 'Ready',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-300',
  },
  delivered: {
    label: 'Delivered',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-300',
  },
  invoiced: {
    label: 'Invoiced',
    bgColor: 'bg-teal-100',
    textColor: 'text-teal-700',
    borderColor: 'border-teal-300',
  },
  cancelled: {
    label: 'Cancelled',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-300',
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className }) => {
  const config = statusConfig[status] || {
    label: status,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        config.bgColor,
        config.textColor,
        config.borderColor,
        'font-medium',
        className
      )}
    >
      {config.label}
    </Badge>
  );
};

// Item completion status badge
interface CompletionStatusBadgeProps {
  status: string;
  className?: string;
}

const completionStatusConfig: Record<
  string,
  { label: string; bgColor: string; textColor: string; borderColor: string }
> = {
  pending: {
    label: 'Pending',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  },
  in_progress: {
    label: 'In Progress',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
  },
  completed: {
    label: 'Completed',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-300',
  },
  skipped: {
    label: 'Skipped',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-300',
  },
};

export const CompletionStatusBadge: React.FC<CompletionStatusBadgeProps> = ({
  status,
  className,
}) => {
  const config = completionStatusConfig[status] || completionStatusConfig.pending;

  return (
    <Badge
      variant="outline"
      className={cn(
        config.bgColor,
        config.textColor,
        config.borderColor,
        'font-medium',
        className
      )}
    >
      {config.label}
    </Badge>
  );
};
